import type { ProblemaContraste } from "@bo/shared";
import { ApiError } from "@/lib/api.js";

/**
 * Traduce un error del API del módulo a lo que la pantalla muestra: un mensaje
 * general, los errores de cada campo (por la ruta del pedido, por ejemplo
 * "otp.idcanalEmisor") y el tipo, para los avisos especiales (permisos de base
 * que faltan, configuración, DDL pendiente, conflicto de versión).
 */

export type TipoError = "grant" | "config" | "ddl" | "conflicto" | "validacion" | "limite" | "permiso" | "red" | "otro";

export interface ErrorInterpretado {
  codigo: string;
  status: number;
  tipo: TipoError;
  titulo: string;
  mensaje: string;
  campos: Record<string, string>;
  faltan: string[];
  contraste: ProblemaContraste[];
}

/** El campo del pedido al que se refiere cada código de negocio del API. */
const CAMPO_DE_CODIGO: Record<string, string> = {
  ETIQUETA_INVALIDA: "etiqueta",
  ETIQUETA_RESERVADA: "etiqueta",
  ETIQUETA_EN_USO: "etiqueta",
  ETIQUETA_EN_CURSO: "etiqueta",
  ETIQUETA_EN_ZONA: "etiqueta",
  COMPANY_NO_HABILITADA: "idcompany",
  COMPANY_INACTIVA: "idcompany",
  SKILL_INVALIDO: "idskill",
  OTP_EMISOR_INVALIDO: "otp.idcanalEmisor",
  OTP_SIN_LLAVE: "otp.idcanalEmisor",
  OTP_EMISOR_OTRA_COMPANY: "otp.idcanalEmisor",
  OTP_REQUERIDO: "otp",
  OTP_PLANTILLA_INEXISTENTE: "otp.plantilla",
  OTP_PLANTILLA_NO_APROBADA: "otp.plantilla",
  OTP_VIGENCIA_DISTINTA: "otp.vigenciaSeg",
  LINK_ORIGEN_INVALIDO: "link.origenes",
  LINK_ORIGENES_REQUERIDOS: "link.origenes",
  BOT_DE_OTRA_COMPANY: "idbot",
  BOT_NO_VERIFICADO: "idbot",
  BOT_SIN_IDBOT: "idbot",
  BOT_NAMEBOT_DESCONOCIDO: "namebot",
  PAISES_INCOHERENTES: "paisesPermitidos",
  TEMA_ORIGEN_INVALIDO: "tema.idcanalOrigen",
  ATAJOS_SIN_LUGAR: "atajos",
  CONFIRMACION_INCORRECTA: "confirmarHost",
};

const TIPO_DE_CODIGO: Record<string, TipoError> = {
  GRANT_MISSING: "grant",
  WEBCHAT_CONFIG_MISSING: "config",
  TB_SECRETO: "config",
  LLAVERO_KID: "config",
  DDL_PENDIENTE: "ddl",
  MARCA_DDL_PENDIENTE: "ddl",
  MARCA_PREFIJO_DESALINEADO: "ddl",
  VERSION_CONFLICTO: "conflicto",
  ESTADO_CAMBIO: "conflicto",
  OPERACION_OCUPADA: "conflicto",
  WEBCHAT_OCUPADO: "conflicto",
  CF_OCUPADO: "conflicto",
  EMISOR_OCUPADO: "conflicto",
  VALIDATION_ERROR: "validacion",
  CONTRASTE: "validacion",
  CHECK_VIOLADO: "validacion",
  RATE_LIMITED: "limite",
  RATE_LIMIT: "limite",
  FORBIDDEN: "permiso",
  COMPANY_NO_HABILITADA: "permiso",
};

const TITULO_DE_TIPO: Record<TipoError, string> = {
  grant: "Faltan permisos de base de datos",
  config: "Falta configurar el servidor",
  ddl: "Falta aplicar un cambio de base",
  conflicto: "Alguien cambió esto mientras tanto",
  validacion: "Hay datos para corregir",
  limite: "Demasiados pedidos seguidos",
  permiso: "Sin permiso para esta acción",
  red: "No se pudo hablar con el servidor",
  otro: "No se pudo completar la acción",
};

interface Detalles {
  fieldErrors?: Record<string, string[] | undefined>;
  formErrors?: string[];
  faltan?: unknown;
  problemas?: unknown;
  check?: unknown;
  idcanales?: unknown;
}

function comoDetalles(d: unknown): Detalles {
  return typeof d === "object" && d !== null ? (d as Detalles) : {};
}

function esProblema(p: unknown): p is ProblemaContraste {
  return typeof p === "object" && p !== null && typeof (p as ProblemaContraste).texto === "string";
}

function camposDeValidacion(d: Detalles): Record<string, string> {
  const campos: Record<string, string> = {};
  for (const [k, mensajes] of Object.entries(d.fieldErrors ?? {})) {
    const primero = mensajes?.[0];
    if (primero) campos[k] = primero;
  }
  return campos;
}

function mensajeDe(err: ApiError, d: Detalles, tipo: TipoError): string {
  if (tipo === "limite") return err.message || "Esperá un momento antes de volver a intentar.";
  if (err.code === "CHECK_VIOLADO" && typeof d.check === "string") {
    return `${err.message} Regla de la base: ${d.check}.`;
  }
  if (err.code === "VALIDATION_ERROR") return d.formErrors?.[0] ?? "Revisá los campos marcados.";
  if (tipo === "otro" && err.status >= 500 && err.code !== "TB_ERROR") return "El servidor tuvo un problema. Probá de nuevo y, si se repite, avisá al equipo.";
  return err.message;
}

function tipoPorEstado(status: number): TipoError {
  if (status === 403) return "permiso";
  if (status === 429) return "limite";
  return "otro";
}

export function interpretarError(err: unknown): ErrorInterpretado {
  if (!(err instanceof ApiError)) {
    return {
      codigo: "RED",
      status: 0,
      tipo: "red",
      titulo: TITULO_DE_TIPO.red,
      mensaje: "Revisá la conexión y probá de nuevo.",
      campos: {},
      faltan: [],
      contraste: [],
    };
  }
  const d = comoDetalles(err.details);
  const tipo = TIPO_DE_CODIGO[err.code] ?? tipoPorEstado(err.status);
  const campos = camposDeValidacion(d);
  const campo = CAMPO_DE_CODIGO[err.code];
  if (campo) campos[campo] = err.message;
  const contraste = Array.isArray(d.problemas) ? d.problemas.filter(esProblema) : [];
  for (const p of contraste) {
    campos[`valores.${p.texto}`] = `Contraste ${p.ratio}:1 (mínimo ${p.minimo}:1). Sugerencia: ${p.sugerencia}`;
  }
  return {
    codigo: err.code,
    status: err.status,
    tipo,
    titulo: TITULO_DE_TIPO[tipo],
    mensaje: mensajeDe(err, d, tipo),
    campos,
    faltan: Array.isArray(d.faltan) ? d.faltan.filter((x): x is string => typeof x === "string") : [],
    contraste,
  };
}

/** Los errores de un ZodError (de los schemas compartidos) por ruta, el primero de cada una. */
export function camposDeZod(issues: readonly { path: (string | number)[]; message: string }[]): Record<string, string> {
  const campos: Record<string, string> = {};
  for (const i of issues) {
    const ruta = i.path.join(".");
    campos[ruta] ??= i.message;
  }
  return campos;
}
